import type { PageMetafields, PageSchema } from '@shopexperts/shared';
import { initialValueForField } from '../field-types';
import { normalizeEntityId, normalizeMetafieldDraftValue } from './metafieldValues';
import { normalizeSuperCollectionPreset } from './superCollectionPreset';

export type PageMetafieldsDraft = Record<string, any>;

export function initializeMetafields(
  schema: PageSchema | null | undefined,
  source: PageMetafields,
): PageMetafieldsDraft {
  const next: PageMetafieldsDraft = {};
  for (const field of schema?.fields || []) {
    const value = source?.[field.key];
    if (field.type === 'image') {
      if (value && typeof value === 'object' && 'url' in value) {
        const imageValue = value as { url: string; id?: string | number };
        next[field.key] = [{ url: imageValue.url, media_id: imageValue.id, isTemporary: false }];
      } else if (typeof value === 'string' && value) {
        // The API serves image fields as the CDN URL plus a `{key}_media`
        // companion object — recover the media id from it, otherwise a
        // save would null the image out.
        const media = source?.[`${field.key}_media`];
        const mediaId = media && typeof media === 'object' && 'id' in media
          ? (media as { id?: string | number }).id
          : undefined;
        next[field.key] = [{ url: value, media_id: mediaId, isTemporary: false }];
      } else {
        next[field.key] = [];
      }
    } else if (value !== undefined) {
      next[field.key] = normalizeMetafieldDraftValue(field, value);
    } else {
      next[field.key] = initialValueForField(field);
    }
  }
  return next;
}

export function buildMetafields(
  schema: PageSchema | null | undefined,
  draft: PageMetafieldsDraft,
): PageMetafields {
  const next: PageMetafields = {};
  for (const field of schema?.fields || []) {
    const value = draft[field.key];
    if (field.type === 'image') {
      if (Array.isArray(value) && value.length > 0) {
        next[field.key] = value[0]?.media_id || null;
      } else {
        next[field.key] = null;
      }
    } else if (field.type === 'partner_badge' || field.type === 'service_category' || field.type === 'service_subcategory') {
      next[field.key] = normalizeEntityId(value);
    } else if (value !== undefined && value !== '') {
      next[field.key] = field.key === 'preset_filters_query'
        ? normalizeSuperCollectionPreset(value).normalized
        : value;
    }
  }
  return next;
}

export interface BuildPreviewMetafieldsOptions {
  /** Metafields of the selected page exactly as the API served them. */
  source?: PageMetafields;
  /** Freshly resolved entity public references, keyed by field key. */
  entities?: Record<string, unknown>;
}

/**
 * Builds the metafields payload for the live preview in the same shape the
 * public API serves on published pages: image fields become a URL plus a
 * `{key}_media` companion, entity fields become their public-reference
 * object (or null while unresolved), matching PageService::transformMetafields.
 */
export function buildPreviewMetafields(
  schema: PageSchema | null | undefined,
  draft: PageMetafieldsDraft,
  options: BuildPreviewMetafieldsOptions = {},
): PageMetafields {
  const { source = {}, entities = {} } = options;
  const next: PageMetafields = {};
  for (const field of schema?.fields || []) {
    const value = draft[field.key];
    if (field.type === 'image') {
      const upload = Array.isArray(value) && value.length > 0 ? value[0] : null;
      const url = upload?.url || null;
      next[field.key] = url;
      const sourceMedia = source[`${field.key}_media`];
      if (upload && sourceMedia && typeof sourceMedia === 'object'
        && (sourceMedia as { id?: unknown }).id === upload.media_id) {
        next[`${field.key}_media`] = sourceMedia;
      } else if (upload) {
        next[`${field.key}_media`] = { id: upload.media_id ?? null, url, cdn_url: url };
      } else {
        next[`${field.key}_media`] = null;
      }
    } else if (field.type === 'partner_badge' || field.type === 'service_category' || field.type === 'service_subcategory') {
      const id = normalizeEntityId(value);
      if (!id) {
        next[field.key] = null;
        continue;
      }
      const resolved = entities[field.key];
      if (resolved && typeof resolved === 'object' && (resolved as { id?: unknown }).id === id) {
        next[field.key] = resolved;
        continue;
      }
      const original = source[field.key];
      if (original && typeof original === 'object' && normalizeEntityId(original) === id) {
        next[field.key] = original;
        continue;
      }
      // Unresolved reference: emit null (like the API does for unknown ids)
      // rather than a raw numeric id no section knows how to render.
      next[field.key] = null;
    } else if (value !== undefined && value !== '') {
      next[field.key] = value;
    }
  }
  return next;
}
